import React, { useEffect, useState } from 'react';
import { getSeedReports, getAllAnalyses } from '../utils/api';
import PlayerHeatmapCard from '../components/PlayerHeatmapCard';
import PlayerDetailModal from '../components/PlayerDetailModal';
import './AnalysisPage.css';

const TOP_PERFORMER_COUNT = 3;

export default function PlayerAnalysisPage() {
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    Promise.all([getSeedReports(), getAllAnalyses().catch(() => ({ data: [] }))]).then(([seedRes, mineRes]) => {
      const userReports = (mineRes.data || []).filter(r => r.status === 'complete' && r.report);
      const all = [];
      [...userReports, ...(seedRes.data || [])].forEach(r => {
        (r.report?.playerPerformances || []).forEach(p => {
          all.push({
            ...p,
            homeTeam: r.homeTeam,
            awayTeam: r.awayTeam,
            matchDate: r.matchDate,
            teamName: p.team === 'home' ? r.homeTeam : r.awayTeam,
          });
        });
      });
      all.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      setPlayers(all);
    });
  }, []);

  const filtered = filter === 'all' ? players : players.filter(p => p.team === filter);
  const topPerformers = filtered.slice(0, TOP_PERFORMER_COUNT);

  return (
    <div className="analysis-page fade-in">
      <div className="analysis-page__header">
        <div>
          <h1>Player Analysis</h1>
          <p>Ratings, zones of play, and AI-graded contributions — every number is from your actual uploaded matches.</p>
        </div>
        <div className="pill-toggle">
          {['all', 'home', 'away'].map(f => (
            <button key={f} className={`pill-toggle__btn ${filter === f ? 'pill-toggle__btn--active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'home' ? 'Home' : 'Away'}
            </button>
          ))}
        </div>
      </div>

      {topPerformers.length > 0 && (
        <div className="analysis-block">
          <h3 className="analysis-block__title">Top Performers</h3>
          <div className="top-performer-grid stagger">
            {topPerformers.map((p, i) => (
              <PlayerHeatmapCard key={i} player={p} color={p.team === 'home' ? '#00d4ff' : '#7c3aed'} onClick={() => setSelected(p)} />
            ))}
          </div>
        </div>
      )}

      <div className="analysis-block">
        <h3 className="analysis-block__title">All Players</h3>
        <div className="player-grid stagger">
          {filtered.map((p, i) => (
            <div key={i} className="card player-analysis-card" onClick={() => setSelected(p)} role="button" tabIndex={0}>
              <div className="pac-header">
                <div className="pac-avatar" style={{ borderColor: p.team === 'home' ? '#00d4ff' : '#7c3aed', color: p.team === 'home' ? '#00d4ff' : '#7c3aed' }}>{p.name?.[0] || '?'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="pac-name">{p.name}</div>
                  <div className="pac-pos">{p.position}</div>
                  <div className="pac-match">{p.homeTeam} vs {p.awayTeam}</div>
                </div>
                <div className="pac-rating" data-h={p.rating >= 8 ? 'high' : p.rating >= 6 ? 'mid' : 'low'}>
                  {p.rating?.toFixed(1)}
                </div>
              </div>
              {p.statsConfidence && (
                <>
                  <div className="pac-confidence-label">
                    <span>Confidence</span>
                    <span>{p.statsConfidence}</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${p.statsConfidence === 'high' ? 90 : p.statsConfidence === 'medium' ? 60 : 30}%` }} />
                  </div>
                </>
              )}
              <div className="pac-click-hint">Click for full details →</div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              Upload match videos to see player analysis
            </div>
          )}
        </div>
      </div>

      <PlayerDetailModal player={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
